/**
 * accountIsolation — pure decision policy for per-account localStorage wiping.
 *
 * Extracted from auth.js so the (security-sensitive) rule is unit-testable in
 * isolation, with no React/DOM dependency.
 *
 * BACKGROUND / RISK BEING FIXED:
 *   `wipePerAccountState()` clears all `jtr_*` browser storage so account A's
 *   cached state can't leak to account B on a shared device. Previously the
 *   "active user" ref was re-initialised to null on every AuthProvider mount,
 *   so a NORMAL page reload that merely restored the SAME user's session
 *   (via checkAuth) looked like a null->user "switch" and wiped everything —
 *   destroying legitimate per-user persistence on every reload.
 *
 * SAFETY ARGUMENT (why null->user must NOT wipe here):
 *   Genuine account *switches* always go through login()/register(), which
 *   call wipePerAccountState() UNCONDITIONALLY before setUserAndIsolate().
 *   The only path that depends on this policy is checkAuth() restoring an
 *   existing session on reload. Therefore:
 *     - same uid                  -> never wipe (it's the user's own data)
 *     - prev uid -> different uid -> wipe (defence in depth; e.g. cookie
 *                                    changed to another account out of band)
 *     - null     -> uid           -> do NOT wipe (fresh restore; switches are
 *                                    already covered by login/register)
 *     - * -> null (logout)        -> clear the persisted uid; do not wipe here
 *                                    (logout() force-wipes separately)
 *
 * @param {string|null} prevUid  last active user id (seeded from storage)
 * @param {string|null} nextUid  incoming user id (or null when logged out)
 * @returns {{wipe:boolean, activeUid:(string|null), persistUid?:string, clearPersist?:boolean}}
 */
export function decideIsolation(prevUid, nextUid) {
  if (!nextUid) {
    return { wipe: false, activeUid: null, clearPersist: true };
  }
  if (nextUid === prevUid) {
    return { wipe: false, activeUid: nextUid, persistUid: nextUid };
  }
  return { wipe: prevUid != null, activeUid: nextUid, persistUid: nextUid };
}

/** localStorage key holding the last active uid. Deliberately OUTSIDE the
 *  `jtr_` namespace so wipePerAccountState() never deletes it. */
export const LAST_UID_KEY = '__jtr_active_uid';
